import React, {useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {useAnalyticsDashboardContext} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsContext';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import AnalyticsBlockOverflowDropdown from 'insights/analytics-queries/overflow-menu/AnalyticsBlockOverflowDropdown';
import {exportAnalyticsQueryOutputToCSV} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import runWarehouseQueryGlobalState from 'insights/analytics-queries/run-query/runWarehouseQueryGlobalState';
import AnalyticsQuerySettingsButton from 'insights/analytics-queries/settings/AnalyticsQuerySettingsButton';
import getVisualizationWarnings from 'insights/analytics-visualizations/getVisualizationWarnings';
import {QUERY_LOADING_MESSAGE} from 'insights/InsightsConstants';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import BlankUniversalBlockView from 'insights/universal-blocks/BlankUniversalBlockView';
import ErrorUniversalBlockView from 'insights/universal-blocks/ErrorUniversalBlockView';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {SharedUniversalBlockViewProps} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {ExportType, Row, RunQueryOutput, RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import convertRunQueryOutputToLegacyOutput, {
  convertRunQueryStateToLegacyState,
} from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import {useWritableContext} from 'insights/WritableContext';
import handleClickShapashTableRow from 'machine-learning/handleClickShapashTableRow';
import isShapashDashboard from 'machine-learning/isShapashDashboard';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ToggleExpandButton from 'ui-library/buttons/ToggleExpandButton';
import {DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import {assertNever} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {getAnalyticsQueryViewStateId} from '../AnalyticsQueryViewState';
import {AnalyticsQuerySpecRequest} from '../RESTAnalyticsQueryTypes';
import AddBlockToAnalysisButton from './AddBlockToAnalysisButton';
import AnalyticsBlockErrorMessage from './AnalyticsBlockErrorMessage';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['analyticsQueries'][number];

interface UnconnectedActions {
  cloneBlock: (analyticsQueryId: string) => void;
  deleteBlock: (analyticsQueryId: string) => void;
  updateAnalyticsQuery: (analyticsQueryId: string, querySpecRequest: AnalyticsQuerySpecRequest) => void;
  updateParameterValues: (newValues: Array<AnalyticsParameterValue>) => void;
  openBlockEditor: (analyticsQueryId: string) => void;
  toggleExpandBlock: (analyticsQueryId: string, newIsExpanded: boolean) => void;
}
interface ConnectedActions {
  exportAnalyticsQueryOutputToCSV: typeof exportAnalyticsQueryOutputToCSV;
}
export type Actions = ConnectedActions & UnconnectedActions;

export interface AnalyticsBlockProps {
  actions: Actions;
  analyticsQuery: AnalyticsQuery;
  blockViewStateId: string;
  dashboardAttrs: Pick<AnalyticsDashboard, 'analyticsQueries' | 'id' | 'name'>;
  dragHandleProps?: DnDDragHandleProps;
  index: number;
  isExpanded: boolean;
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
  runQueryState: RunQueryState;
}

/**
 * AnalyticsBlock is the base component of analytics dashboards when they are in dashboard editor
 * (i.e. when users can see multiple visualizations).
 *
 * The width of the blocks is controlled by it's layout setter. For the main Insights dashboard editor, the
 * minimum width of blocks is stored in InsightsConstants.ts
 */
export function AnalyticsBlock({
  actions,
  analyticsQuery,
  blockViewStateId,
  dashboardAttrs,
  dragHandleProps,
  index,
  isExpanded,
  parameterConfigs,
  parameterValues,
  runQueryState,
}: AnalyticsBlockProps): JSX.Element {
  const legacyRunQueryState = convertRunQueryStateToLegacyState(runQueryState);
  const writable = useWritableContext();
  const {
    delayExpensiveRenders,
    theme: {paneBorder},
  } = useAnalyticsDashboardContext();

  const warnings = useMemo(
    () =>
      legacyRunQueryState.status === AsyncStatus.SUCCESS
        ? getVisualizationWarnings(legacyRunQueryState.data)
        : [],
    [legacyRunQueryState]
  );

  function handleOpenBlockEditor(): void {
    actions.openBlockEditor(analyticsQuery.id);
  }

  function handleToggleExpandOnChange(newIsExpanded: boolean): void {
    actions.toggleExpandBlock(analyticsQuery.id, newIsExpanded);
  }

  const analyticsBlockToolbar = (
    <>
      {writable && (
        <Tooltip title="Edit block">
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            icon="edit"
            onClick={handleOpenBlockEditor}
            size={ButtonSize.MINI}
            className="u-mini-spacing-left"
            dataTestAttrs={{component: 'AnalyticsBlock', element: 'editButton'}}
          />
        </Tooltip>
      )}
      {writable && (
        <AnalyticsQuerySettingsButton
          actions={actions}
          analyticsQuery={analyticsQuery}
          buttonSize={ButtonSize.MINI}
          className="u-mini-spacing-left"
        />
      )}
      <AddBlockToAnalysisButton
        analyticsQueryId={analyticsQuery.id}
        analyticsQueryName={analyticsQuery.name}
        sqlQuery={analyticsQuery.sqlQuery}
        dashboardId={dashboardAttrs.id}
        parameterConfigs={parameterConfigs}
        parameterValues={parameterValues}
        disabled={runQueryState.status !== AsyncStatus.SUCCESS}
      />
      <AnalyticsBlockOverflowDropdown
        actions={{
          cloneBlock: () => {
            actions.cloneBlock(analyticsQuery.id);
          },
          deleteBlock: () => {
            actions.deleteBlock(analyticsQuery.id);
          },
          exportBlock: (exportType: ExportType) => {
            actions.exportAnalyticsQueryOutputToCSV(analyticsQuery, parameterConfigs, parameterValues, {
              dashboardName: dashboardAttrs.name,
              exportType,
            });
          },
        }}
        blockViewStateId={blockViewStateId}
        className="u-mini-spacing-left"
        dashboardAttrs={dashboardAttrs}
        runQueryState={runQueryState}
      />
      <ToggleExpandButton
        className="u-mini-spacing-left"
        isExpanded={isExpanded}
        onChange={handleToggleExpandOnChange}
      />
    </>
  );

  const sharedUniversalBlockViewProps: SharedUniversalBlockViewProps = {
    paneProps: {
      description: analyticsQuery.description,
      dragHandleProps: writable ? dragHandleProps : undefined,
      paneBorder,
      title: analyticsQuery.name,
      toolbar: analyticsBlockToolbar,
      warnings,
      dataTestAttrs: {component: 'AnalyticsBlock', index: `${index}`, element: 'root'},
    },
  };

  function renderUniversalBlock(runQueryOutput: RunQueryOutput | null): JSX.Element {
    if (runQueryOutput == null) {
      return <BlankUniversalBlockView {...sharedUniversalBlockViewProps} />;
    }

    return (
      <PresentationalUniversalBlockView
        {...sharedUniversalBlockViewProps}
        actionOrigin={ActionOrigin.INSIGHTS}
        inputDataFrame={runQueryOutput.dataFrame}
        isLegacyInsights={true}
        renderOptions={{
          delayExpensiveRenders,
        }}
        viewStateId={blockViewStateId}
        visualizationConfig={analyticsQuery.visualization}
        experimentalShapashTableOptions={
          isShapashDashboard(dashboardAttrs)
            ? {
                onClick: (row: Row) => {
                  handleClickShapashTableRow(
                    row,
                    convertRunQueryOutputToLegacyOutput(runQueryOutput),
                    parameterConfigs,
                    parameterValues,
                    actions.updateParameterValues
                  );
                },
              }
            : undefined
        }
      />
    );
  }

  const errorMessage = <AnalyticsBlockErrorMessage openBlockEditor={handleOpenBlockEditor} />;
  switch (runQueryState.status) {
    case AsyncStatus.SUCCESS:
    case AsyncStatus.LOADING:
    case AsyncStatus.NOT_FETCHED:
      return (
        <LoadingOverlay
          message={QUERY_LOADING_MESSAGE}
          isLoading={runQueryState.status !== AsyncStatus.SUCCESS}
        >
          {renderUniversalBlock(
            runQueryState.status === AsyncStatus.SUCCESS
              ? runQueryState.data
              : runQueryState.status === AsyncStatus.LOADING
                ? runQueryState.previousData
                : null
          )}
        </LoadingOverlay>
      );
    case AsyncStatus.ERROR:
      return <ErrorUniversalBlockView {...sharedUniversalBlockViewProps} errorMessage={errorMessage} />;
    default:
      assertAndContinue(false, `Unexpected runQueryState status: ${assertNever(runQueryState)}`);
      return <ErrorUniversalBlockView {...sharedUniversalBlockViewProps} errorMessage={errorMessage} />;
  }
}

export default function ConnectedAnalyticsBlock(
  props: Omit<AnalyticsBlockProps, 'actions' | 'blockViewStateId' | 'runQueryState'> & {
    actions: UnconnectedActions;
  }
): JSX.Element {
  const {analyticsDashboardViewStateId} = useAnalyticsDashboardContext();
  const blockViewStateId = getAnalyticsQueryViewStateId(
    props.analyticsQuery.id,
    analyticsDashboardViewStateId
  );

  const [runQueryState] = runWarehouseQueryGlobalState.useGlobalClientState(blockViewStateId);
  return (
    <AnalyticsBlock
      {...props}
      actions={{...props.actions, exportAnalyticsQueryOutputToCSV}}
      blockViewStateId={blockViewStateId}
      runQueryState={runQueryState}
    />
  );
}
